import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { Observable, Subject, Subscription, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { isEqual, isNil, sortBy } from 'lodash';

import { ControlValueAccessorModel, getProvider } from '../../../Common/Forms/models/control-value-accessor.model';
import { InputValidationModel } from '../../../Common/Forms/models/input-validation.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { BrandIdentityService } from '../../../Common/BrandIdentity/services/brand-identity.service';
import { BrandIdentity } from '../../../Common/BrandIdentity/models/brand-identity';
import {
    ManageBrandIdentitiesDialogComponent
} from '../../../Common/BrandIdentity/components/ManageBrandIdentitiesDialog/manage-brand-identities-dialog.component';
import { BrandInfoConfig } from '../../../Common/BrandIdentity/components/BrandCustomInformation/brand-custom-information.component';
import { ScrollPanelComponent } from '../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { buildSlideInAnimationTrigger } from '../../../Core/Dialogs/models/full-profile-animation.model';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';

import { BrandCustomInfo } from '../../../api/brandIdentity/models/brand-identity.api.model';
import { ViBrandContextApi } from '../../../api/visibilityIntelligence/models/vi-brand-context.api.model';

import { ViBrandIdentity } from '../../models/vi-brand-identity.model';

import { ViBrandCardService } from '../../services/vi-brand-card.service';

@Component({
    selector: 'agpr-vi-brand-identity-card',
    templateUrl: './vi-brand-identity-card.component.html',
    styleUrls: ['./vi-brand-identity-card.component.scss'],
    standalone: false,
    providers: [getProvider(ViBrandIdentityCardComponent)],
    animations: [
        trigger('cardState', [
            state('collapsed', style({
                height: '0'
            })),
            state('expanded', style({
                height: '*'
            })),
            transition('collapsed <=> expanded', animate('220ms ease-in'))
        ])
    ]
})
export class ViBrandIdentityCardComponent extends ControlValueAccessorModel<ViBrandIdentity> implements OnInit, OnChanges, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;
    readonly websiteChipValidations: InputValidationModel[] = [
        new InputValidationModel({
            requirements: /^(http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@%!\$&'\(\)\*\+,;=.]+$/,
            exclusions: null,
            errorKey: 'Common.brandInformation.socialLinks.url.invalid'
        })
    ];
    readonly defaultColor: string = '#0D74BA';
    readonly brandAutocompleteCustomOptions: AutocompleteCustomOption[] = [
        {
            label: 'VisibilityIntelligence.brandSetupPanel.brandField.createBrand',
            action: (name: string) => {
                const newBrand = new BrandIdentity();
                newBrand.name = name;

                const dialogClosed$ = this.openManageBrandsDialog(newBrand);

                let createdBrandId: number;
                this.brandIdentityService.actionCompleted$.pipe(
                    filter(resourceAction => resourceAction.actionType === ResourceActionTypes.CREATE && !resourceAction.error),
                    take(1),
                    takeUntil(dialogClosed$)
                ).subscribe(resourceAction => {
                    createdBrandId = resourceAction.resource?.id;
                });

                dialogClosed$.pipe(
                    take(1),
                    filter(() => !isNil(createdBrandId)),
                    switchMap(() => this.brandList$.pipe(
                        map(brands => brands.find(brand => brand.id === createdBrandId)),
                        filter(brand => !!brand),
                        take(1)
                    ))
                ).subscribe((createdBrand: BrandIdentity) => {
                    this.selectBrand(createdBrand);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];
    readonly additionalInfoConfig: BrandInfoConfig = {
        label: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo',
        placeholder: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo.placeholder',
        typeLabel: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo.typeLabel',
        typePlaceholder: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo.typePlaceholder',
        titleLabel: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo.titleLabel',
        titlePlaceholder: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo.titlePlaceholder',
        titleRequired: false,
        infoLabel: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo',
        infoPlaceholder: 'VisibilityIntelligence.brandSetupPanel.card.addtionalInfo.infoPlaceholder',
        infoRequired: false,
        useTextArea: true,
    };
    readonly additionalInfoActions: MenuItem[] = this.viBrandCardService.buildAdditionalInfoActions();

    @Input() id: string = 'viBrandIdentityCard';
    @Input() showExtendedFields: boolean = true;
    @Input() interactive: boolean = true;
    @Input() removable: boolean = false;
    @Input() displayErrors: boolean = false;
    @Input() disabled: boolean = false;
    // A competitor can't share the main brand's name or another competitor's name; set only on competitor cards.
    @Input() excludedNames: string[] = [];

    @Output() brandSelected = new EventEmitter<BrandIdentity | null>();
    @Output() removed = new EventEmitter<void>();

    subscriptions: Subscription[] = [];
    brands: BrandIdentity[] = [];
    brandList$: Observable<BrandIdentity[]>;

    isExpanded: boolean = true;
    brand: BrandIdentity | null = null;
    logoLoadError: boolean = false;
    isEditingName: boolean = false;
    nameInputValue: string = '';
    name: string = '';
    placeholderText: string = '';
    nameVariations: string[] = [];
    websites: string[] = [];
    industry: string = '';
    bio: string = '';
    additionalInfo: BrandCustomInfo[] = [];

    hasNameError: boolean = false;
    hasDuplicateNameError: boolean = false;
    hasWebsitesError: boolean = false;
    hasIndustryError: boolean = false;
    hasBioError: boolean = false;

    private pendingBrandId?: number;
    // the last brand-derived value of each field, used to detect whether the user has since customized it
    private brandName: string = '';
    private brandNameVariations: string[] = [];
    private brandWebsites: string[] = [];
    private brandIndustry: string = '';
    private brandBio: string = '';
    private brandAdditionalInfo: BrandCustomInfo[] = [];

    constructor(
        private coreDialogService: CoreDialogService,
        private viBrandCardService: ViBrandCardService,
        private brandIdentityService: BrandIdentityService,
        private elementRef: ElementRef,
    ) {
        super();
        this.brandList$ = this.brandIdentityService.all$;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.brandList$.subscribe(brands => {
                this.brands = brands ?? [];
                if (isNil(this.pendingBrandId)) {
                    return;
                }
                const brand = this.brands.find(item => item.id === this.pendingBrandId);
                if (brand) {
                    this.brand = brand;
                    this.logoLoadError = false;
                    this.pendingBrandId = undefined;
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.excludedNames) {
            this.hasDuplicateNameError = this.isDuplicateName(this.name);
        }
    }

    override writeValue(value: ViBrandIdentity): void {
        this.value = value ?? { name: '', nameVariations: [] } as ViBrandIdentity;

        this.name = this.value.name ?? '';
        this.placeholderText = this.getPlaceholderText(this.name);
        this.nameVariations = [...(this.value.nameVariations ?? [])];
        this.websites = [...(this.value.websites ?? [])];
        this.industry = this.value.industry ?? '';
        this.bio = this.value.bio ?? '';
        this.additionalInfo = (this.value.additionalContext ?? [])
            .map(ctx => ({ title: ctx.contextTitle, info: ctx.contextInfo } as BrandCustomInfo));

        this.brandName = this.name;
        this.brandNameVariations = [...this.nameVariations];
        this.brandWebsites = [...this.websites];
        this.brandIndustry = this.industry;
        this.brandBio = this.bio;
        this.brandAdditionalInfo = this.additionalInfo.map(info => ({ ...info }));

        this.hasNameError = false;
        this.hasDuplicateNameError = false;
        this.hasWebsitesError = false;
        this.hasIndustryError = false;
        this.hasBioError = false;

        this.brand = isNil(this.value.brandId) ? null : this.brands.find(brand => brand.id === this.value.brandId) ?? null;
        this.pendingBrandId = this.brand ? undefined : this.value.brandId;
        this.logoLoadError = false;
    }

    override setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    manageBrandIdentity(): void {
        this.openManageBrandsDialog(this.brand ?? new BrandIdentity());
    }

    selectBrand(brand: BrandIdentity): void {
        if (!brand) {
            this.brand = null;
            this.logoLoadError = false;
            this.emitValue();
            return;
        }

        const hasCustomName = !!this.name?.trim() && this.name !== this.brandName;
        if (!hasCustomName) {
            this.brandName = brand.name ?? '';
            this.name = this.brandName;
            this.placeholderText = this.getPlaceholderText(this.name);
            this.hasNameError = !this.name?.trim();
            this.hasDuplicateNameError = this.isDuplicateName(this.name);
        }

        const hasCustomNameVariations = !!this.nameVariations?.length
            && !isEqual([...this.nameVariations].sort(), [...this.brandNameVariations].sort());
        if (!hasCustomNameVariations) {
            this.brandNameVariations = brand.nameVariations ?? [];
            this.nameVariations = [...this.brandNameVariations];
        }

        const hasCustomWebsites = !!this.websites?.length
            && !isEqual([...this.websites].sort(), [...this.brandWebsites].sort());
        if (!hasCustomWebsites) {
            this.brandWebsites = brand.websites ?? [];
            this.websites = [...this.brandWebsites];
            this.hasWebsitesError = !this.websites?.length;
        }

        if (this.showExtendedFields) {
            const hasCustomIndustry = !!this.industry?.trim() && this.industry !== this.brandIndustry;
            if (!hasCustomIndustry) {
                this.brandIndustry = brand.industry ?? '';
                this.industry = this.brandIndustry;
                this.hasIndustryError = !this.industry?.trim();
            }

            const hasCustomBio = !!this.bio?.trim() && this.bio !== this.brandBio;
            if (!hasCustomBio) {
                this.brandBio = brand.bio ?? '';
                this.bio = this.brandBio;
                this.hasBioError = !this.bio?.trim();
            }

            this.additionalInfoActions
                .filter(action => !action.isCustomType)
                .forEach(action => this.mergeAdditionalInfo(action.label ?? '', this.viBrandCardService.getBrandInfoValue(brand, action.key)));
            this.mergeCustomAdditionalInfo(brand.customInfo ?? []);

            this.brandAdditionalInfo = [
                ...this.additionalInfoActions
                    .filter(action => !action.isCustomType)
                    .map(action => ({ title: action.label ?? '', info: this.viBrandCardService.getBrandInfoValue(brand, action.key) }))
                    .filter(entry => !!entry.info),
                ...(brand.customInfo ?? []).map(info => ({ title: info.title, info: info.info }))
            ];
        }

        this.brand = brand;
        this.logoLoadError = false;
        this.brandSelected.emit(brand);
        this.emitValue();
    }

    validate(): boolean {
        this.hasNameError = !this.name?.trim();
        this.hasDuplicateNameError = this.isDuplicateName(this.name);
        this.hasWebsitesError = !this.websites?.length;
        if (this.showExtendedFields) {
            this.hasIndustryError = !this.industry?.trim();
            this.hasBioError = !this.bio?.trim();
        }

        const isValid = !this.hasNameError && !this.hasDuplicateNameError && !this.hasWebsitesError
            && (!this.showExtendedFields || (!this.hasIndustryError && !this.hasBioError));

        return isValid;
    }

    scrollFirstErrorIntoView(scrollPanel?: ScrollPanelComponent): void {
        this.isExpanded = true;
        requestAnimationFrame(() => {
            let fieldIdSuffix: string | null = null;
            if (this.hasNameError || this.hasDuplicateNameError) {
                fieldIdSuffix = '_name';
            } else if (this.hasWebsitesError) {
                fieldIdSuffix = '_websites';
            } else if (this.hasIndustryError) {
                fieldIdSuffix = '_industry';
            } else if (this.hasBioError) {
                fieldIdSuffix = '_bio';
            }
            if (!fieldIdSuffix) {
                return;
            }
            const element: HTMLElement | null = this.elementRef.nativeElement.querySelector(`#${CSS.escape(this.id + fieldIdSuffix)}`);
            if (element) {
                ScrollPanelComponent.scrollElementIntoView(element, scrollPanel);
            }
        });
    }

    doneEditingName(): void {
        const trimmed = this.nameInputValue?.trim();
        if (!trimmed || trimmed === this.name) {
            this.isEditingName = false;
            return;
        }
        this.nameChanged(trimmed);
    }

    nameChanged(value: string): void {
        this.name = value;
        this.placeholderText = this.getPlaceholderText(this.name);
        this.hasNameError = !value?.trim();
        this.hasDuplicateNameError = this.isDuplicateName(value);
        this.isEditingName = false;
        this.emitValue();
    }

    nameVariationsChanged(value: string[]): void {
        this.nameVariations = value;
        this.emitValue();
    }

    websitesChanged(value: string[]): void {
        this.websites = value;
        this.hasWebsitesError = !value?.length;
        this.emitValue();
    }

    industryChanged(value: string): void {
        this.industry = value;
        this.hasIndustryError = !value?.trim();
        this.emitValue();
    }

    bioChanged(value: string): void {
        this.bio = value;
        this.hasBioError = !value?.trim();
        this.emitValue();
    }

    additionalInfoChanged(value: BrandCustomInfo[]): void {
        this.additionalInfo = value;
        this.emitValue();
    }

    remove(): void {
        this.removed.emit();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private getPlaceholderText(name: string): string {
        const trimmed = name?.trim() ?? '';
        if (!trimmed) {
            return '';
        }
        const words = trimmed.split(' ');
        if (words.length === 1) {
            return trimmed.slice(0, Math.min(trimmed.length, 2)).toLocaleUpperCase();
        }
        return words[0][0].toLocaleUpperCase() + words[words.length - 1][0].toLocaleUpperCase();
    }

    private openManageBrandsDialog(brand: BrandIdentity): Observable<void> {
        this.brandIdentityService.setCurrentResource(brand);

        const dialogClosed$ = new Subject<void>();
        this.coreDialogService.open(ManageBrandIdentitiesDialogComponent, {
            inputs: {
                overlayContainer: document.getElementById('main-content'),
                animationTrigger: buildSlideInAnimationTrigger()
            },
            outputs: {
                dialogClose: () => {
                    dialogClosed$.next();
                    this.brandIdentityService.setCurrentResource(new BrandIdentity());
                }
            }
        });

        return dialogClosed$.asObservable();
    }

    private mergeAdditionalInfo(label: string, value: string): void {
        const index = this.additionalInfo.findIndex(item => item.title === label);
        const cachedInfo = this.brandAdditionalInfo.find(item => item.title === label)?.info ?? '';
        const isCustomized = index >= 0 && !!this.additionalInfo[index].info?.trim() && this.additionalInfo[index].info !== cachedInfo;

        if (isCustomized) {
            return;
        }
        if (!value) {
            if (index >= 0) {
                this.additionalInfo = this.additionalInfo.filter((_, i) => i !== index);
            }
            return;
        }
        this.additionalInfo = index >= 0
            ? this.additionalInfo.map((item, i) => i === index ? { ...item, info: value } : item)
            : [...this.additionalInfo, { title: label, info: value }];
    }

    private mergeCustomAdditionalInfo(brandCustomInfo: BrandCustomInfo[]): void {
        const customLabels = this.additionalInfoActions.filter(action => !action.isCustomType).map(action => action.label);
        const currentCustomInfo = this.additionalInfo.filter(item => !customLabels.includes(item.title));
        const cachedCustomInfo = this.brandAdditionalInfo.filter(item => !customLabels.includes(item.title));

        const hasCustomCustomInfo = !!currentCustomInfo.length
            && !isEqual(sortBy(currentCustomInfo, ['title', 'info']), sortBy(cachedCustomInfo, ['title', 'info']));

        if (hasCustomCustomInfo) {
            return;
        }

        const readonlyEntries = this.additionalInfo.filter(item => customLabels.includes(item.title));
        this.additionalInfo = [...readonlyEntries, ...brandCustomInfo.map(info => ({ title: info.title, info: info.info }))];
    }

    private emitValue(): void {
        this.value = {
            id: this.value?.id,
            brandId: this.brand?.id,
            name: this.name,
            nameVariations: this.nameVariations,
            websites: this.websites,
            ...(this.showExtendedFields ? {
                industry: this.industry,
                bio: this.bio,
                additionalContext: this.additionalInfo.map(info => ({ contextTitle: info.title, contextInfo: info.info } as ViBrandContextApi))
            } : {})
        } as ViBrandIdentity;
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    private isDuplicateName(value: string): boolean {
        const trimmed = value?.trim().toLowerCase();
        if (!trimmed) {
            return false;
        }
        return this.excludedNames.some(name => name?.trim().toLowerCase() === trimmed);
    }
}
